import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ONLINE_HEADER } from '@evorait/pace-offline';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HTTP_REQUEST_METHOD } from '../../enums/http-request-method.enum';
import {
    HttpOptions,
    isResponseWithResults,
    OdataResponse,
    OnlineOnlyInterface,
} from '../../interfaces/online-only.interface';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { HttpUtilitiesService } from '../http-utilities/http-utilities.service';

@Injectable()
export class OnlineOnlyService implements OnlineOnlyInterface {
    public constructor(
        private readonly http: HttpClient,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly httpUtilities: HttpUtilitiesService,
    ) {}

    /**
     * Get data from endpoint and return promise
     *
     * @param query the query string to be appended to the base uri
     * @returns the observable of the response
     */
    public get<T>(query: string): Observable<T[] | T> {
        return this.request<T>(this.uriUtilities.getUriForContent() + query, HTTP_REQUEST_METHOD.GET);
    }

    /**
     * Post json payload to endpoint and return promise
     *
     * @param  {string} query the query string to be appended to the base uri
     * @param  {object} payload payload that can be stringified
     * @returns the observable of the response
     */
    public post<T>(query: string, payload: Record<string, unknown>): Observable<T[] | T> {
        return this.request<T>(this.uriUtilities.getUriForContent() + query, HTTP_REQUEST_METHOD.POST, payload);
    }

    /**
     * Send put request to backend
     *
     * @param query the query string to be appended to the base uri
     * @param payload the payload that can be stringified
     * @returns the observable of the response
     */
    public put<T>(query: string, payload: Record<string, unknown>): Observable<undefined> {
        return this.request<T>(this.uriUtilities.getUriForContent() + query, HTTP_REQUEST_METHOD.PUT, payload);
    }

    /**
     * Actually run the request using the $http service.
     *
     * @param {string} url the url to be called
     * @param {string} method the method to be used
     * @param {object} [payload] only given for POST or PUT
     * @returns the observable of the response
     * @private
     */
    private request<T>(
        url: string,
        method: HTTP_REQUEST_METHOD.GET | HTTP_REQUEST_METHOD.POST,
        payload?: Record<string, unknown>,
    ): Observable<T[] | T>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private request<T>(
        url: string,
        method: HTTP_REQUEST_METHOD.PUT,
        payload?: Record<string, unknown>,
    ): Observable<undefined>;

    private request<T>(
        url: string,
        method: HTTP_REQUEST_METHOD.GET | HTTP_REQUEST_METHOD.POST | HTTP_REQUEST_METHOD.PUT,
        payload?: Record<string, unknown>,
    ): Observable<T[] | T | undefined> {
        const options: HttpOptions = {
            // with this option we get access to the entire response object, including the HTTP status code
            // if we omit this option, the response will only contain the body of the actual response
            observe: 'response',
        };
        if (payload) {
            options.body = payload;
        }

        // this header is required so that offline interceptor can identify that it is a "online-only" request
        let reqHeaders = new HttpHeaders({ [ONLINE_HEADER]: ONLINE_HEADER });

        // https://evorait.atlassian.net/browse/SHCNGMAAP2-858
        // Adding headers for idempotent GW services to avoid duplicate requests for POST and PUT calls.
        if (method === HTTP_REQUEST_METHOD.POST || method === HTTP_REQUEST_METHOD.PUT) {
            reqHeaders = this.httpUtilities.setHeadersForIdempotency(reqHeaders);
        }

        options.headers = reqHeaders;

        // no need to set headers as this is automatically done by the online-only http interceptor
        return this.http.request(method, url, options).pipe(
            map((response: HttpResponse<OdataResponse<T>>) => {
                this.log.debug(`OnlineOnlyService: ${method} request to ${url} successful: Status ${response.status}`);

                // If everything is fine, hand over response data
                switch (method) {
                    case HTTP_REQUEST_METHOD.GET:
                    case HTTP_REQUEST_METHOD.POST:
                        if (response.body && response.body.d) {
                            if (isResponseWithResults<T>(response.body.d)) {
                                return response.body.d.results;
                            } else {
                                return response.body.d;
                            }
                        } else {
                            throw new Error(
                                `OnlineOnlyService: Error in response to ${method} request to ${url}: Status ${response.status}, response is missing data`,
                            );
                        }
                    case HTTP_REQUEST_METHOD.PUT: {
                        const putResponseErrorCode = 204;
                        if (response.status === putResponseErrorCode) {
                            return undefined;
                        } else {
                            throw new Error(
                                `OnlineOnlyService: Error in response to put request to ${url}: Status ${
                                    response.status
                                }, ${JSON.stringify(response)}`,
                            );
                        }
                    }
                }
            }),
        );
    }
}
